// External libraries
import { useCallback, useEffect, useMemo, useState } from 'react';
import { get } from 'lodash';
import * as Sentry from '@sentry/browser';

// BWA
import {
    elementNames,
    errorNames,
    elementMetricNames,
    componentNames,
    EMAIL_TAKEN_MESSAGE,
} from './constants';
import { elementNames as patientInfoElementNames } from '../PatientInfoForm/constants';
import {
    validateConfirmEmailBlizzard,
    getValidator,
} from '../../utils/CreateAccountValidation';

import {
    RULE_STATUS,
    passwordGuess,
    passwordLength,
    passwordLetters,
    passwordNumbers,
    passwordSymbols,
    passwordRepetitive,
    passwordWhiteSpace,
} from '../../utils/CreateAccountValidation/constants';
import { EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';

function pad(d) {
    const i = Number(d);
    return i < 10 ? '0' + i.toString() : i.toString();
}

const buildPostBody = (form, directoryId) => {
    const month = pad(form.birthMonth);
    const day = pad(form.birthDay);
    return {
        accepted_hipaa_authorization: false,
        accepted_terms_of_use: form.termsOfService,
        date_of_birth: `${form.birthYear}-${month}-${day}`,
        email: form.email,
        first_name: form.firstName,
        gender: form.sex,
        last_name: form.lastName,
        remember_me: form.rememberMe === undefined ? true : form.rememberMe,
        password: form.password,
        directory_id: directoryId,
    };
};

export const useSignUpForm = ({
    goBackToPatientInfo,
    onSubmitSuccess,
    patientInfoContext,
    sendMetric,
    directoryId,
}) => {
    const [focused, setFocused] = useState(null);

    const [touched, setTouched] = useState({
        [elementNames.email]: false,
        [elementNames.confirmEmail]: false,
        [elementNames.password]: false,
        [elementNames.termsOfService]: false,
        [elementNames.rememberMe]: false,
    });

    const [values, setValues] = useState({
        [elementNames.email]: '',
        [elementNames.confirmEmail]: '',
        [elementNames.password]: '',
        [elementNames.termsOfService]: false,
        [elementNames.rememberMe]: true,
    });

    const [invalidFields, setInvalidFields] = useState({
        [elementNames.email]: true,
        [elementNames.confirmEmail]: true,
        [elementNames.password]: true,
        [elementNames.termsOfService]: true,
        [elementNames.rememberMe]: false,
    });

    const [passwordStatusChecks, setStatuses] = useState({
        [passwordLength]: RULE_STATUS.UNSET,
        [passwordNumbers]: RULE_STATUS.UNSET,
        [passwordSymbols]: RULE_STATUS.UNSET,
        [passwordLetters]: RULE_STATUS.UNSET,
        [passwordGuess]: RULE_STATUS.UNSET,
        [passwordRepetitive]: RULE_STATUS.UNSET,
        [passwordWhiteSpace]: RULE_STATUS.UNSET,
    });

    const [emailAlreadyInUseError, setEmailAlreadyInUseError] = useState(false);

    const [
        serverSideWeakPasswordError,
        setServerSideWeakPasswordError,
    ] = useState(false);
    const [inlineEmailLoading, setInlineEmailLoading] = useState(false);

    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleFocus = useCallback(e => {
        if (e) {
            // Until React 17 we need this to be able to access the e.target.name,
            // see https://reactjs.org/docs/legacy-event-pooling.html
            e.persist();
            setFocused(e.target.name);
        }
    }, []);

    const handleClickLink = e => {
        const name = e.target.name;
        const element = elementMetricNames[name];
        const component = componentNames[name];
        sendMetric({
            action: `Clicked ${element.toLowerCase()}`,
            component,
            element,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };

    const handleChange = useCallback(
        async e => {
            if (e) {
                e.persist();
                const { name, value } = e.target;
                /* values is an object React probably compares by reference,
                 * so update it only if it really changed to avoid rerenders
                 */

                setValues(values =>
                    values[name] === value
                        ? values
                        : { ...values, [name]: value }
                );
                const validate = getValidator(name);

                const isInvalid =
                    name === elementNames.confirmEmail
                        ? !(await validateConfirmEmailBlizzard(
                              value,
                              values[elementNames.email]
                          ))
                        : !(await validate(value));

                /* invalidFields is an object React probably compares by reference,
                 * so update it only if it really changed to avoid rerenders
                 */
                setInvalidFields(invalidFields =>
                    invalidFields[name] === isInvalid
                        ? invalidFields
                        : {
                              ...invalidFields,
                              [name]: isInvalid,
                          }
                );

                // Each time the value of email field changes, revalidate confirm email field
                if (name === elementNames.email) {
                    setEmailAlreadyInUseError(false); // clear email already in use error

                    const isInvalid = !(await validateConfirmEmailBlizzard(
                        values[elementNames.confirmEmail],
                        value
                    ));

                    setInvalidFields(invalidFields =>
                        invalidFields[elementNames.confirmEmail] === isInvalid
                            ? invalidFields
                            : {
                                  ...invalidFields,
                                  [elementNames.confirmEmail]: isInvalid,
                              }
                    );
                } else if (name === elementNames.password) {
                    setServerSideWeakPasswordError(false);
                    const passwordStatusCheck = getValidator(
                        elementNames.passwordStatusCheck
                    );
                    const newStatuses = await passwordStatusCheck(value);

                    setStatuses(newStatuses);
                }
            }
        },
        [values]
    );

    const handleChangeCheckbox = useCallback(
        async e => {
            if (e) {
                e.persist();
                const name = e.target.name;
                const checked = e.target.checked;

                setValues(values =>
                    values[name] === checked
                        ? values
                        : { ...values, [name]: checked }
                );

                if (name === elementNames.termsOfService) {
                    const validator = getValidator(elementNames.termsOfService);
                    const isInvalid = !(await validator(checked));
                    setInvalidFields(invalidFields =>
                        invalidFields[elementNames.termsOfService] === isInvalid
                            ? invalidFields
                            : {
                                  ...invalidFields,
                                  [elementNames.termsOfService]: isInvalid,
                              }
                    );
                }

                // reason we have to call setTouched after setValue and setInvalidFields
                // we can trust React to update the state in the same order as setState is called for
                // ref: https://stackoverflow.com/questions/48563650/does-react-keep-the-order-for-state-updates
                setTouched(touched =>
                    touched[name] ? touched : { ...touched, [name]: true }
                );
                const element = elementMetricNames[name];
                const component = componentNames[name];
                sendMetric({
                    action: checked
                        ? `Checked ${element}`
                        : `Unchecked ${element}`,
                    component,
                    element: 'Checkbox',
                    initiator: EVENT_INITIATORS.USER,
                    interactionType: INTERACTION_TYPES.SELECT,
                });
            }
        },
        [sendMetric]
    );

    const emailError = useMemo(() => {
        return (
            touched[elementNames.email] &&
            (!values[elementNames.email] || invalidFields[elementNames.email])
        );
    }, [touched, values, invalidFields]);

    useEffect(() => {
        if (emailError) {
            sendMetric({
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
                component: elementMetricNames[elementNames.email],
                element: 'Error',
                action: 'Saw email error',
            });
        }
    }, [emailError, sendMetric]);

    const confirmEmailError = useMemo(() => {
        return (
            !emailError &&
            !emailAlreadyInUseError &&
            touched[elementNames.confirmEmail] &&
            (!values[elementNames.confirmEmail] ||
                invalidFields[elementNames.confirmEmail])
        );
    }, [emailAlreadyInUseError, emailError, invalidFields, touched, values]);

    useEffect(() => {
        if (confirmEmailError) {
            sendMetric({
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
                component: elementMetricNames[elementNames.confirmEmail],
                element: 'Error',
                action: 'Saw confirm email error',
            });
        }
    }, [confirmEmailError, sendMetric]);

    const passwordError = useMemo(() => {
        return (
            touched[elementNames.password] &&
            (!values[elementNames.password] ||
                invalidFields[elementNames.password])
        );
    }, [invalidFields, touched, values]);

    useEffect(() => {
        if (passwordError) {
            sendMetric({
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
                component: elementMetricNames[elementNames.password],
                element: 'Error',
                action: 'Saw password error',
            });
        }
    }, [passwordError, sendMetric]);

    const termsOfServiceError = useMemo(() => {
        return (
            touched[elementNames.termsOfService] &&
            (!values[elementNames.termsOfService] ||
                invalidFields[elementNames.termsOfService])
        );
    }, [touched, values, invalidFields]);

    useEffect(() => {
        if (termsOfServiceError) {
            sendMetric({
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
                component: elementMetricNames[elementNames.termsOfService],
                element: 'Error',
                action: 'Saw Terms of Use & Privacy Policy required error',
            });
        }
    }, [termsOfServiceError, sendMetric]);

    useEffect(() => {
        if (emailAlreadyInUseError && !emailError) {
            // only when there's no email invalid error,
            // we show email already in use error
            sendMetric({
                action: 'Saw email already in use error',
                component: componentNames.emailAlreadyInUseError,
                element: 'Error',
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
        }
    }, [emailAlreadyInUseError, emailError, sendMetric]);

    useEffect(() => {
        if (submitError) {
            sendMetric({
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
                element: 'Error',
                component: 'Creation failed try again error',
                action: 'Saw account creation failed try again error',
            });
        }
    }, [sendMetric, submitError]);

    const errors = {
        [errorNames.emailError]: emailError,
        [errorNames.confirmEmailError]: confirmEmailError,
        // it's not necessary bc we have password rule list,
        // but maybe useful later for the common password errors
        [errorNames.passwordError]: passwordError,
        [errorNames.serverSideWeakPasswordError]: serverSideWeakPasswordError,
        [errorNames.submitError]: submitError,
        [errorNames.termsOfServiceError]: termsOfServiceError,
    };

    const handleInlineEmailValidation = useCallback(async email => {
        const apiHost = process.env.API_HOST || '';
        const validationEndpoint = `${apiHost}/api/web/2/patient/validateemail`;
        const fetchData = {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: email }),
            credentials: 'include',
        };
        setInlineEmailLoading(true);
        const resp = await fetch(validationEndpoint, fetchData).catch(() => {
            // handles request timeouts and CORS errors
        });

        if (resp.status === 200) {
            setEmailAlreadyInUseError(false);
        } else if (resp.status === 400) {
            const val = await resp.json().catch(() => {});
            const fields = get(val, 'error.fields', []);
            const messages = fields.map(field => field.message);

            if (messages.includes(EMAIL_TAKEN_MESSAGE)) {
                setEmailAlreadyInUseError(true);
            }
        }

        setInlineEmailLoading(false);
    }, []);

    const handleBlur = useCallback(
        async e => {
            if (e) {
                // Until React 17 we need this to be able to access the e.target.name,
                // see https://reactjs.org/docs/legacy-event-pooling.html
                e.persist();
                /* touched is an object React probably compares by reference,
                 * so update it only if it really changed to avoid rerenders
                 */

                if (e.target.value) {
                    const element = elementMetricNames[e.target.name];
                    const component = componentNames[e.target.name];
                    sendMetric({
                        action: `Typed ${element.toLowerCase()}`,
                        component,
                        element,
                        initiator: EVENT_INITIATORS.USER,
                        interactionType: INTERACTION_TYPES.TYPE,
                    });
                }

                const updatedTouches = touched =>
                    touched[e.target.name]
                        ? touched
                        : { ...touched, [e.target.name]: true };
                setTouched(updatedTouches);

                // Only do the inline email check if user entered a valid email
                if (
                    e.target.name === elementNames.email &&
                    !invalidFields[elementNames.email]
                ) {
                    await handleInlineEmailValidation(e.target.value);
                }
            }

            setFocused(null);
        },
        [handleInlineEmailValidation, invalidFields, sendMetric]
    );

    const submitPatientCreationRequest = useCallback(async () => {
        const formData = {
            ...patientInfoContext.values,
            ...values,
        };

        // TODO: handle rememberMe
        const requestBody = buildPostBody(formData, directoryId);

        const fetchData = {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
            credentials: 'include',
        };
        let responseStatus;

        try {
            const response = await fetch(
                `${process.env.API_HOST || ''}/api/web/2/patient`,
                fetchData
            );

            responseStatus = response.status;

            if (responseStatus === 201) {
                const { email, password, rememberMe } = formData;
                onSubmitSuccess({ email, password, rememberMe });
            }

            if (responseStatus === 400) {
                const body = await response.json();

                let sendBackToPatientInfo = false;
                body.error.fields.forEach(async errorField => {
                    if (errorField.name === 'email') {
                        setEmailAlreadyInUseError(true);
                    } else if (errorField.name === 'password') {
                        setServerSideWeakPasswordError(true);
                        setInvalidFields(invalidFields =>
                            invalidFields[elementNames.password] === true
                                ? invalidFields
                                : {
                                      ...invalidFields,
                                      [elementNames.password]: true,
                                  }
                        );
                    } else if (errorField.name === 'first_name') {
                        sendBackToPatientInfo = true;
                        patientInfoContext.setInvalidFields(invalidFields => ({
                            ...invalidFields,
                            [patientInfoElementNames.firstName]: true,
                        }));
                    } else if (errorField.name === 'last_name') {
                        sendBackToPatientInfo = true;
                        patientInfoContext.setInvalidFields(invalidFields => ({
                            ...invalidFields,
                            [patientInfoElementNames.lastName]: true,
                        }));
                    } else if (errorField.name === 'date_of_birth') {
                        sendBackToPatientInfo = true;
                        patientInfoContext.setServerBirthDateError(true);
                    }
                });
                if (sendBackToPatientInfo) {
                    goBackToPatientInfo();
                }
            }
        } catch (e) {
            Sentry.captureException(e);
            setSubmitError(true);
        }
    }, [
        directoryId,
        goBackToPatientInfo,
        onSubmitSuccess,
        patientInfoContext,
        values,
    ]);

    const handleClickSubmit = useCallback(async () => {
        sendMetric({
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
            component: componentNames[elementNames.reviewAndBook],
            element: elementMetricNames[elementNames.reviewAndBook],
            action: 'Clicked Review & Book',
        });

        if (
            !touched[elementNames.password] ||
            values[elementNames.password] === ''
        ) {
            const passwordStatusCheck = getValidator(
                elementNames.passwordStatusCheck
            );
            const newStatuses = await passwordStatusCheck('');

            setStatuses(newStatuses);
        }

        setSubmitted(true);
        setTouched({
            [elementNames.email]: true,
            [elementNames.confirmEmail]: true,
            [elementNames.password]: true,
            [elementNames.termsOfService]: true,
        });

        const formHasInvalidFields = Object.entries(invalidFields)
            .map(entry => {
                // eslint-disable-next-line no-unused-vars
                const [_, isInvalid] = entry;
                return isInvalid;
            })
            .some(Boolean);

        if (!formHasInvalidFields && !emailAlreadyInUseError) {
            /* These errors can be set by request handler if necessary
             * on the next account creation request, but the current
             * value if any becomes irrelevant once we leave this page
             * because it's from the context of the last failed request
             */
            setEmailAlreadyInUseError(false);
            setSubmitError(false);

            /* Send the data to the server and handle response
             */
            setLoading(true);
            /* This has to be awaited for testingbecause it is actually
             * an asynchronous callback function that awaits an HTTP request,
             * not just a React state setter
             */
            await submitPatientCreationRequest();
            setLoading(false);
        }
    }, [
        sendMetric,
        touched,
        values,
        invalidFields,
        emailAlreadyInUseError,
        submitPatientCreationRequest,
    ]);

    const highlightedFields = useMemo(() => {
        return Object.entries(invalidFields).reduce(
            (highlightedFields, invalidFieldsEntry) => {
                const [name, isInvalid] = invalidFieldsEntry;
                const isHighlighted =
                    isInvalid && touched[name] && focused !== name;
                return { ...highlightedFields, [name]: isHighlighted };
            },
            {}
        );
    }, [focused, invalidFields, touched]);

    return {
        errors,
        handleBlur,
        handleChange,
        handleClickSubmit,
        handleFocus,
        handleClickLink,
        highlightedFields,
        loading,
        passwordStatusChecks,
        handleChangeCheckbox,
        touched,
        values,
        submitted,
        inlineEmailLoading,
        emailAlreadyInUseError,
    };
};

export default useSignUpForm;
